import { FC } from 'react'
import { Home, Package, Building2, Wrench } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/auth.store'
import { useTranslation } from '../hooks/useTranslation'

interface BottomNavigationProps {
  projectId?: string
  equipmentUuid?: string
}

const BottomNavigation: FC<BottomNavigationProps> = ({ projectId, equipmentUuid }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { isAuthenticated, isInstaller } = useAuthStore()

  const isMainHomeActive = location.pathname === '/go'
  const isProductHomeActive = location.pathname === `/view/${equipmentUuid}`
  const isMyProjectsActive = location.pathname === '/my-projects'
  const isProjectActive = location.pathname.startsWith('/project/') || isMyProjectsActive
  const isInstallerDashboardActive = location.pathname === '/installer-dashboard'

  // An authenticated installer can always reach the list of his own projects,
  // even when no project is selected yet.
  const canBrowseProjects = isAuthenticated && isInstaller

  const tabs = [
    {
      icon: Home,
      label: t('BOTTOM_NAV_HOME'),
      active: isMainHomeActive,
      onClick: () => navigate('/go'),
      disabled: false,
    },
    {
      icon: Package,
      label: t('BOTTOM_NAV_PRODUCT_HOME'),
      active: isProductHomeActive,
      onClick: () => equipmentUuid && navigate(`/view/${equipmentUuid}`),
      disabled: !equipmentUuid,
    },
    {
      icon: Building2,
      label: t('BOTTOM_NAV_PROJECT'),
      active: isProjectActive,
      onClick: () => {
        if (projectId) navigate(`/project/${projectId}`)
        else if (equipmentUuid) navigate(`/project/from-equipment/${equipmentUuid}`)
        else if (canBrowseProjects) navigate('/my-projects')
      },
      disabled: (!projectId && !canBrowseProjects) || isProjectActive,
    },
  ]

  if (isAuthenticated && isInstaller) {
    tabs.push({
      icon: Wrench,
      label: t('INSTALLER_DASHBOARD'),
      active: isInstallerDashboardActive,
      onClick: () => navigate('/installer-dashboard'),
      disabled: false,
    })
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-[var(--brand-dark)] border-t border-white/10">
      <div className="flex items-center justify-around h-16 px-2 pb-[env(safe-area-inset-bottom)]">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.label}
              onClick={tab.onClick}
              disabled={tab.disabled}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                tab.disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <Icon className={`h-5 w-5 ${tab.active ? 'text-white' : 'text-[var(--brand-grey)]'}`} />
              <span className={`text-[10px] font-medium ${tab.active ? 'text-white' : 'text-[var(--brand-grey)]'}`}>
                {tab.label}
              </span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}

export default BottomNavigation
